import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import * as styles from './index.module.less';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { Paragraph } from '../heading';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper';

interface IProps {
  data: {
    featureDescList: {
      title: string;
      desc: string;
      cover: {
        src: string;
        alt: string;
      };
      tips?: string;
    }[];
  };
  titleActiveColor?: string;
  descActiveColor?: string;
  swiperBulletColor?: string;
  swiperBulletActiveColor?: string;
}

const FeatureItemContent = ({ activeIndex, index, titleActiveColor, descActiveColor, data, onClick = () => {} }) => {
  const { t } = useTranslation();
  return (
    <div className={`${styles.feature_content_item} ${activeIndex === index && styles.active}`} onClick={onClick}>
      <div className={styles.feature_title} style={activeIndex === index ? { backgroundImage: titleActiveColor } : {}}>
        {t(data.title)}
      </div>
      <Paragraph
        className={styles.feature_desc}
        style={{
          color: descActiveColor,
        }}
      >
        {t(data.desc)}
      </Paragraph>
    </div>
  );
};

const FeatureItemCover = ({ activeIndex, index, data }) => {
  const { t } = useTranslation();
  return (
    <div className={`${styles.cover_item_wrapper} ${activeIndex === index && styles.active}`}>
      <img className={styles.feature_cover} src={data.cover.src} alt={t(data.cover.alt)} />
    </div>
  );
};

const TipsItem = ({ data }) => {
  const { t } = useTranslation();
  return <div className={styles.tips_item}>{t(data)}</div>;
};

const MoreFeaturesDisplay = ({
  data,
  titleActiveColor = 'linear-gradient(0deg,#FF369E 0%, #FF369E 100%)',
  descActiveColor = '#46505C',
  swiperBulletColor = '#eeeff0',
  swiperBulletActiveColor = '#f97fba',
}: IProps) => {
  const { featureDescList } = data;
  const tipsList = [];
  featureDescList.forEach((item) => {
    if (item?.tips && !tipsList.includes(item.tips)) {
      tipsList.push(item.tips);
    }
  });
  const [activeIndex, setActiveIndex] = useState(0);
  const DELAY = 5 * 1000;
  const swiperRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => {
      const nextActiveIndex = (activeIndex + 1) % featureDescList.length;
      setActiveIndex(nextActiveIndex);
      swiperRef.current?.swiper?.slideTo(nextActiveIndex);
    }, DELAY);

    return () => {
      clearInterval(timer);
    };
  }, [DELAY, activeIndex, featureDescList.length]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.content_wrapper}>
        {/* 功能介绍 */}
        <div className={styles.feature_content_wrapper}>
          {featureDescList.map((item, index) => {
            return (
              <FeatureItemContent
                activeIndex={activeIndex}
                index={index}
                key={item.title}
                titleActiveColor={titleActiveColor}
                descActiveColor={descActiveColor}
                data={item}
                onClick={() => {
                  setActiveIndex(index);
                }}
              />
            );
          })}
        </div>
        {/* tips */}
        <div className={styles.tips_wrapper}>
          {tipsList.map((item) => {
            return <TipsItem key={item.title} data={item} />;
          })}
        </div>
      </div>
      <div className={styles.cover_wrapper}>
        {featureDescList.map((item, index) => {
          return <FeatureItemCover activeIndex={activeIndex} index={index} key={index} data={item} />;
        })}
      </div>
      {/* md & xs & xxs */}
      <div
        className={styles.swiper_wrapper}
        style={
          {
            '--swiper-bullet-color': swiperBulletColor,
            '--swiper-bullet-active-color': swiperBulletActiveColor,
          } as CSSProperties
        }
      >
        <Swiper
          ref={swiperRef}
          className={styles.swiper_self}
          modules={[Pagination]}
          pagination={{
            clickable: true,
            bulletActiveClass: styles.swiper_bullet_active,
            bulletClass: styles.swiper_bullet,
          }}
          breakpoints={{
            0: {
              spaceBetween: 10,
            },
            // when window width is >= 320px
            320: {
              spaceBetween: 20,
            },
            // when window width is >= 480px
            480: {
              spaceBetween: 30,
            },
            // when window width is >= 640px
            640: {
              spaceBetween: 40,
            },
          }}
          onSlideChange={(swiper) => {
            setActiveIndex(swiper.activeIndex);
          }}
        >
          {featureDescList.map((item, index) => {
            return (
              <SwiperSlide key={item.title} className={styles.swiper_slice}>
                <div className={styles.slice_content_wrapper}>
                  <div className={styles.slice_content_text_wrapper}>
                    <FeatureItemContent
                      activeIndex={activeIndex}
                      index={index}
                      titleActiveColor={titleActiveColor}
                      descActiveColor={descActiveColor}
                      data={item}
                    />
                    {item?.tips && <TipsItem data={item.tips} />}
                  </div>
                  <FeatureItemCover activeIndex={activeIndex} index={index} key={item.title} data={item} />
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </div>
  );
};
export default MoreFeaturesDisplay;
